"use client";

import React from "react";
import { useResizeHook } from "../../../lib";
import { CategoryDetails, Category, CategoryPoint, SubCategory } from "../../../types";
import ServicesHighlightsContent from "./ServicesHighlightsContent";
import ServicesHighlightsGallery from "./ServicesHighlightsGallery";
import {
  servicesHighlightsTokens,
  type ServicesHighlightsMaxWidthToken,
} from "./ServicesHighlights.tokens";

export type CategoryHighlightsIcon = React.ReactNode | React.ElementType;

export type CategoryHighlightsLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type CategoryHighlightsCta = {
  label?: string;
  href?: string;
  ariaLabel?: string;
  icon?: CategoryHighlightsIcon;
  iconSrc?: string;
  iconAlt?: string;
};

export type CategoryHighlightsImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  originalAlt?: string;
  thumbnailAlt?: string;
  objectPosition?: string;
  loading?: "eager" | "lazy";
  decoding?: "async" | "auto" | "sync";
};

export type CategoryHighlightsFeature = Partial<SubCategory> & {
  id?: string | number;
  label?: string;
  href?: string;
  image?: CategoryHighlightsImage;
  imageSrc?: string;
  imageAlt?: string;
  icon?: CategoryHighlightsIcon;
  iconSrc?: string;
  iconAlt?: string;
  accentColor?: string;
};

export type CategoryHighlightsBadge = {
  id?: string | number;
  title?: string;
  label?: string;
  text?: string;
  icon?: CategoryHighlightsIcon;
  iconSrc?: string;
  iconAlt?: string;
  accentColor?: string;
};

export type CategoryHighlightsVariant = "feature-panel" | "legacy";

export type CategoryHighlightsProps = {
  category: CategoryDetails & Category;
  alternate?: boolean;
  classes?: string;
  className?: string;
  alternateBackground?: boolean;
  hideServicesLinks?: boolean;
  showContactsButton?: boolean;
  showMainCategoryLink?: boolean;
  showPoints?: boolean;
  hideTitle?: boolean;
  hideSubtitle?: boolean;
  showThumbnails?: boolean;
  showGalleryArrows?: boolean;
  overlineTitle?: string;
  variant?: CategoryHighlightsVariant;
  LinkComponent?: CategoryHighlightsLinkComponent;
  title?: string;
  description?: string;
  features?: CategoryHighlightsFeature[];
  badges?: CategoryHighlightsBadge[];
  primaryCta?: CategoryHighlightsCta;
  secondaryCta?: CategoryHighlightsCta;
  mainCategoryLinkLabel?: string;
  image?: CategoryHighlightsImage;
  eyebrowIcon?: CategoryHighlightsIcon;
  accentColor?: string;
  backgroundColor?: string;
  panelBackgroundColor?: string;
  maxWidth?: ServicesHighlightsMaxWidthToken | number | string;
  contentMaxWidth?: number | string;
  ariaLabel?: string;
};

type ServicesHighlightsCssVars = React.CSSProperties & {
  "--services-highlights-bg"?: string;
  "--services-highlights-panel-bg"?: string;
  "--services-highlights-accent"?: string;
  "--services-highlights-max-width"?: string;
  "--services-highlights-content-max-width"?: string;
};

type NormalizedFeature = {
  id: string | number;
  title: string;
  href: string;
  image?: CategoryHighlightsImage;
  imageSrc?: string;
  imageAlt?: string;
  icon?: CategoryHighlightsIcon;
  iconSrc?: string;
  iconAlt?: string;
  accentColor?: string;
};

type NormalizedBadge = {
  id: string | number;
  title: string;
  text?: string;
  icon?: CategoryHighlightsIcon;
  iconSrc?: string;
  iconAlt?: string;
  accentColor?: string;
};

const t = servicesHighlightsTokens;

const servicesHighlightsCss = `
.services-highlights {
  --services-highlights-bg: ${t.colors.background};
  --services-highlights-panel-bg: ${t.colors.panel};
  --services-highlights-accent: ${t.colors.accent};
  --services-highlights-accent-soft: color-mix(in srgb, var(--services-highlights-accent) 10%, white);
  --services-highlights-accent-border: color-mix(in srgb, var(--services-highlights-accent) 34%, transparent);
  --services-highlights-accent-shadow: color-mix(in srgb, var(--services-highlights-accent) 22%, transparent);
  --services-highlights-max-width: ${t.maxWidth.xl};
  --services-highlights-content-max-width: ${t.sizes.contentMaxWidth}px;
  width: 100%;
  overflow: hidden;
  padding: ${t.spacing.sectionY}px ${t.spacing.sectionX}px;
  background: var(--services-highlights-bg);
  color: ${t.colors.text};
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}
.services-highlights,
.services-highlights * {
  box-sizing: border-box;
}
.services-highlights-shell {
  width: 100%;
  max-width: var(--services-highlights-max-width);
  margin: 0 auto;
}
.services-highlights-panel {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(420px, 1.08fr);
  align-items: center;
  gap: ${t.spacing.desktopGap}px;
  width: 100%;
  overflow: hidden;
  padding: ${t.spacing.panelY}px ${t.spacing.panelX}px;
  border: 1px solid ${t.colors.panelBorder};
  border-radius: ${t.radius.panel}px;
  background: var(--services-highlights-panel-bg);
  box-shadow: ${t.colors.shadow};
}
.services-highlights--alternate .services-highlights-content {
  order: 2;
}
.services-highlights--alternate .services-highlights-media-column {
  order: 1;
}
.services-highlights--no-media .services-highlights-panel {
  grid-template-columns: minmax(0, 1fr);
}
.services-highlights--no-media .services-highlights-content {
  max-width: 820px;
}
.services-highlights-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  max-width: var(--services-highlights-content-max-width);
}
.services-highlights-kicker {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 20px;
  color: var(--services-highlights-accent);
  font-size: ${t.typography.overline.fontSize}px;
  line-height: ${t.typography.overline.lineHeight};
  font-weight: ${t.typography.overline.fontWeight};
  letter-spacing: ${t.typography.overline.letterSpacing};
  text-transform: ${t.typography.overline.textTransform};
}
.services-highlights-kicker-icon,
.services-highlights-feature-icon,
.services-highlights-badge-icon,
.services-highlights-primary-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: var(--services-highlights-accent);
  background: var(--services-highlights-accent-soft);
}
.services-highlights-kicker-icon {
  width: ${t.sizes.iconBadge}px;
  height: ${t.sizes.iconBadge}px;
  border-radius: ${t.radius.icon}px;
}
.services-highlights-kicker-icon svg {
  width: 24px;
  height: 24px;
}
.services-highlights-title {
  max-width: 100%;
  margin: 0;
  color: ${t.colors.text};
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
}
.services-highlights-description {
  max-width: 600px;
  margin: 20px 0 0;
  color: ${t.colors.textSoft};
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
}
.services-highlights-features {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${t.spacing.featureGap}px;
  width: 100%;
  margin-top: 26px;
}
.services-highlights-feature-link {
  min-width: 0;
  color: inherit;
  text-decoration: none;
}
.services-highlights-feature {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 62px;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${t.colors.featureBorder};
  border-radius: ${t.radius.tile}px;
  background: ${t.colors.panel};
  box-shadow: 0 8px 22px rgba(8, 23, 53, 0.04);
  transition: ${t.motion.tile};
}
.services-highlights-feature--with-icon {
  grid-template-columns: ${t.sizes.featureIcon}px minmax(0, 1fr);
  gap: 14px;
  min-height: 72px;
}
.services-highlights-feature--with-image {
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 8px;
  min-height: 74px;
  padding-left: 10px;
  overflow: visible;
}
.services-highlights-feature-image-frame {
  display: block;
  width: 52px;
  height: 52px;
  margin-left: -18px;
  overflow: hidden;
  border-radius: 999px;
  background: ${t.colors.mediaFallback};
  box-shadow: 0 10px 20px rgba(8, 23, 53, 0.12);
  transform: scale(1.8);
  transform-origin: center;
}
.services-highlights-feature-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.services-highlights-feature-link:hover .services-highlights-feature,
.services-highlights-feature-link:focus-visible .services-highlights-feature {
  transform: translateY(-2px);
  border-color: var(--services-highlights-accent-border);
  box-shadow: ${t.colors.softShadow};
}
.services-highlights-feature-link:focus-visible,
.services-highlights-primary-cta:focus-visible,
.services-highlights-secondary-cta:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--services-highlights-accent) 24%, transparent);
  outline-offset: 4px;
  border-radius: ${t.radius.tile}px;
}
.services-highlights-feature-icon {
  width: ${t.sizes.featureIcon}px;
  height: ${t.sizes.featureIcon}px;
  border-radius: ${t.radius.icon}px;
}
.services-highlights-feature-icon svg,
.services-highlights-badge-icon svg {
  width: 23px;
  height: 23px;
}
.services-highlights-icon-image {
  display: block;
  width: 23px;
  height: 23px;
  object-fit: contain;
}
.services-highlights-feature-title {
  min-width: 0;
  margin: 0;
  color: ${t.colors.text};
  font-size: ${t.typography.featureTitle.fontSize}px;
  line-height: ${t.typography.featureTitle.lineHeight};
  font-weight: ${t.typography.featureTitle.fontWeight};
  letter-spacing: ${t.typography.featureTitle.letterSpacing};
}
.services-highlights-ctas {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${t.spacing.ctaGap}px;
  margin-top: 30px;
}
.services-highlights-primary-cta,
.services-highlights-secondary-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  white-space: nowrap;
}
.services-highlights-primary-cta {
  min-height: ${t.sizes.ctaHeight}px;
  gap: 14px;
  padding: 0 24px;
  border-radius: ${t.radius.button}px;
  background: var(--services-highlights-accent);
  color: #ffffff;
  font-size: ${t.typography.cta.fontSize}px;
  line-height: ${t.typography.cta.lineHeight};
  font-weight: ${t.typography.cta.fontWeight};
  letter-spacing: ${t.typography.cta.letterSpacing};
  box-shadow: 0 14px 24px var(--services-highlights-accent-shadow);
  transition: ${t.motion.cta};
}
.services-highlights-primary-cta--with-icon {
  padding-right: 14px;
}
.services-highlights-primary-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px color-mix(in srgb, var(--services-highlights-accent) 28%, transparent);
}
.services-highlights-primary-icon {
  width: 38px;
  height: 38px;
  border-radius: ${t.radius.icon}px;
  background: #ffffff;
}
.services-highlights-secondary-cta {
  gap: 10px;
  min-height: 42px;
  color: var(--services-highlights-accent);
  font-size: ${t.typography.secondaryCta.fontSize}px;
  line-height: ${t.typography.secondaryCta.lineHeight};
  font-weight: ${t.typography.secondaryCta.fontWeight};
  letter-spacing: ${t.typography.secondaryCta.letterSpacing};
}
.services-highlights-secondary-icon-image {
  display: block;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  object-fit: contain;
}
.services-highlights-secondary-cta svg,
.services-highlights-secondary-icon-image {
  transition: transform 180ms ease;
}
.services-highlights-secondary-cta:hover svg,
.services-highlights-secondary-cta:hover .services-highlights-secondary-icon-image {
  transform: translateX(3px);
}
.services-highlights-media-column {
  position: relative;
  min-width: 0;
}
.services-highlights-media {
  position: relative;
  height: ${t.sizes.mediaMinHeight}px;
  min-height: ${t.sizes.mediaMinHeight}px;
  overflow: hidden;
  border-radius: ${t.radius.media}px;
  background: ${t.colors.mediaFallback};
}
.services-highlights-image,
.services-highlights-image-fallback {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 0;
}
.services-highlights-image {
  object-fit: cover;
  transition: ${t.motion.image};
}
.services-highlights-media:hover .services-highlights-image {
  transform: scale(1.035);
}
.services-highlights-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: ${t.colors.textMuted};
  font-size: 16px;
  line-height: 1.4;
  font-weight: 800;
  text-align: center;
  background: linear-gradient(135deg, #f8fbff 0%, #e8f0fb 100%);
}
.services-highlights-media-gallery,
.services-highlights-media-gallery .image-gallery,
.services-highlights-media-gallery .image-gallery-content,
.services-highlights-media-gallery .image-gallery-slide-wrapper,
.services-highlights-media-gallery .image-gallery-swipe,
.services-highlights-media-gallery .image-gallery-slides,
.services-highlights-media-gallery .image-gallery-slide,
.services-highlights-media-gallery .image-gallery-image {
  height: 100%;
  min-height: 0;
}
.services-highlights-media-gallery .image-gallery-image {
  object-fit: cover;
}
.services-highlights-media-gallery .image-gallery-bullets {
  bottom: 14px;
}
.services-highlights-badges {
  position: absolute;
  top: 50%;
  right: -34px;
  z-index: 2;
  display: grid;
  gap: 14px;
  width: ${t.sizes.badgeWidth}px;
  transform: translateY(-50%);
}
.services-highlights-badge {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 58px;
  padding: 12px 14px;
  border: 1px solid ${t.colors.panelBorder};
  border-radius: ${t.radius.badge}px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: ${t.colors.softShadow};
  backdrop-filter: blur(14px);
}
.services-highlights-badge--with-icon {
  grid-template-columns: ${t.sizes.badgeIcon}px minmax(0, 1fr);
  gap: 12px;
  min-height: 68px;
}
.services-highlights-badge-icon {
  width: ${t.sizes.badgeIcon}px;
  height: ${t.sizes.badgeIcon}px;
  border-radius: ${t.radius.icon}px;
}
.services-highlights-badge-text {
  min-width: 0;
}
.services-highlights-badge--with-icon .services-highlights-badge-text {
  padding-left: 10px;
  border-left: 1px solid ${t.colors.badgeDivider};
}
.services-highlights-badge-title,
.services-highlights-badge-copy {
  margin: 0;
}
.services-highlights-badge-title {
  color: ${t.colors.text};
  font-size: ${t.typography.badgeTitle.fontSize}px;
  line-height: ${t.typography.badgeTitle.lineHeight};
  font-weight: ${t.typography.badgeTitle.fontWeight};
  letter-spacing: ${t.typography.badgeTitle.letterSpacing};
}
.services-highlights-badge-copy {
  margin-top: 3px;
  color: ${t.colors.textMuted};
  font-size: 11px;
  line-height: 1.35;
  font-weight: 600;
}
.services-highlights-inline-badges {
  display: none;
  width: 100%;
  margin-top: 22px;
}
@media (max-width: 1280px) {
  .services-highlights-panel {
    grid-template-columns: minmax(0, 0.95fr) minmax(380px, 1.05fr);
    gap: 40px;
    padding: 40px;
  }
  .services-highlights-badges {
    right: -16px;
  }
}
@media (max-width: 1080px) {
  .services-highlights-panel {
    grid-template-columns: minmax(0, 1fr);
  }
  .services-highlights--alternate .services-highlights-content,
  .services-highlights-content {
    order: 1;
    max-width: 760px;
  }
  .services-highlights--alternate .services-highlights-media-column,
  .services-highlights-media-column {
    order: 2;
  }
  .services-highlights-badges {
    right: 18px;
  }
  .services-highlights-media {
    height: 460px;
    min-height: 460px;
  }
}
@media (max-width: 760px) {
  .services-highlights {
    padding: ${t.spacing.mobileSectionY}px ${t.spacing.mobileSectionX}px;
  }
  .services-highlights-panel {
    gap: 24px;
    padding: ${t.spacing.mobilePanelY}px ${t.spacing.mobilePanelX}px;
    border-radius: 18px;
  }
  .services-highlights-kicker {
    gap: 10px;
    margin-bottom: 16px;
    font-size: 12px;
    line-height: 1.25;
  }
  .services-highlights-kicker-icon {
    width: 38px;
    height: 38px;
  }
  .services-highlights-kicker-icon svg {
    width: 21px;
    height: 21px;
  }
  .services-highlights-title {
    font-size: 30px;
    line-height: 1.08;
  }
  .services-highlights-description {
    margin-top: 14px;
    font-size: 14px;
    line-height: 1.55;
  }
  .services-highlights-features {
    grid-template-columns: minmax(0, 1fr);
    margin-top: 22px;
  }
  .services-highlights-feature {
    min-height: 58px;
    padding: 12px;
    gap: 10px;
  }
  .services-highlights-feature--with-icon {
    min-height: 66px;
    gap: 12px;
  }
  .services-highlights-feature--with-image {
    grid-template-columns: 54px minmax(0, 1fr);
    min-height: 68px;
    gap: 8px;
    padding-left: 10px;
  }
  .services-highlights-feature-image-frame {
    width: 48px;
    height: 48px;
    margin-left: -16px;
    transform: scale(1.7);
  }
  .services-highlights-feature-title {
    font-size: 14px;
  }
  .services-highlights-ctas {
    width: 100%;
    gap: 12px;
    margin-top: 24px;
  }
  .services-highlights-primary-cta {
    width: 100%;
    justify-content: space-between;
    min-height: 52px;
    padding-left: 20px;
  }
  .services-highlights-secondary-cta {
    min-height: 36px;
  }
  .services-highlights-media,
  .services-highlights-image,
  .services-highlights-image-fallback,
  .services-highlights-media-gallery,
  .services-highlights-media-gallery .image-gallery,
  .services-highlights-media-gallery .image-gallery-content,
  .services-highlights-media-gallery .image-gallery-slide-wrapper,
  .services-highlights-media-gallery .image-gallery-swipe,
  .services-highlights-media-gallery .image-gallery-slides,
  .services-highlights-media-gallery .image-gallery-slide,
  .services-highlights-media-gallery .image-gallery-image {
    height: ${t.sizes.mobileMediaMinHeight}px;
    min-height: ${t.sizes.mobileMediaMinHeight}px;
  }
  .services-highlights-media {
    border-radius: 18px;
  }
  .services-highlights-badges {
    position: static;
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
    margin-top: 16px;
    transform: none;
  }
  .services-highlights-badge {
    min-height: 56px;
  }
  .services-highlights-badge--with-icon {
    min-height: 64px;
  }
}
@media (max-width: 420px) {
  .services-highlights-panel {
    padding-left: 14px;
    padding-right: 14px;
  }
  .services-highlights-title {
    font-size: 28px;
  }
  .services-highlights-feature--with-icon {
    grid-template-columns: 40px minmax(0, 1fr);
  }
  .services-highlights-feature--with-image {
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 8px;
    padding-left: 8px;
  }
  .services-highlights-feature-image-frame {
    width: 44px;
    height: 44px;
    margin-left: -14px;
    transform: scale(1.7);
  }
  .services-highlights-feature-icon,
  .services-highlights-badge-icon {
    width: 40px;
    height: 40px;
  }
  .services-highlights-feature-icon svg,
  .services-highlights-badge-icon svg,
  .services-highlights-icon-image {
    width: 21px;
    height: 21px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function resolveMaxWidth(value: CategoryHighlightsProps["maxWidth"]): string {
  if (!value) return t.maxWidth.xl;
  if (typeof value === "number") return getCssSize(value);
  if (value in t.maxWidth) return t.maxWidth[value as ServicesHighlightsMaxWidthToken];
  return value;
}

function getDescription(category: CategoryDetails & Category, description?: string): string {
  if (description) return description;
  if (Array.isArray(category.description)) return category.description.join(" ");
  if (typeof category.description === "string") return category.description;
  return "";
}

function getImageSource(image?: CategoryHighlightsImage): string {
  if (!image) return "";
  return image.src || image.url || image.original || image.thumbnail || "";
}

function getImageAlt(image: CategoryHighlightsImage | undefined, fallback: string): string {
  return image?.alt || image?.originalAlt || image?.thumbnailAlt || image?.title || fallback;
}

function getFeatureImage(feature: CategoryHighlightsFeature): CategoryHighlightsImage | undefined {
  if (feature.image) return feature.image;
  if (feature.imageSrc) {
    return {
      src: feature.imageSrc,
      alt: feature.imageAlt,
    };
  }
  return feature.images?.[0] as CategoryHighlightsImage | undefined;
}

function getCategoryImage(category: CategoryDetails & Category, image?: CategoryHighlightsImage): CategoryHighlightsImage | undefined {
  if (image) return image;
  if (category.heroImage?.url) {
    return {
      src: category.heroImage.url,
      alt: category.heroImage.alt,
      title: category.heroImage.title,
    };
  }
  return category.images?.[0] as CategoryHighlightsImage | undefined;
}

function getFeatureTitle(feature: CategoryHighlightsFeature): string {
  return feature.label || feature.title || feature.name || "Service";
}

function getFeatureHref(feature: CategoryHighlightsFeature, category: CategoryDetails & Category): string {
  if (feature.href) return feature.href;
  if (feature.link) return feature.link;
  if (feature.slug) return `/services/${category.slug}/${feature.slug}`;
  return `/services/${category.slug}`;
}

function isRenderableIconComponent(icon: CategoryHighlightsIcon): icon is React.ElementType {
  return (
    typeof icon === "function" ||
    (typeof icon === "object" &&
      icon !== null &&
      "$$typeof" in icon &&
      !React.isValidElement(icon))
  );
}

function hasIconContent(icon: CategoryHighlightsIcon | undefined): boolean {
  return icon !== undefined && icon !== null && icon !== false;
}

function hasProvidedIcon(iconSrc?: string, icon?: CategoryHighlightsIcon): boolean {
  return !!iconSrc || hasIconContent(icon);
}

function renderIcon(
  icon: CategoryHighlightsIcon | undefined,
  className: string,
  size: number
) {
  if (!hasIconContent(icon)) {
    return null;
  }

  const iconProps = {
    size,
    strokeWidth: 2.25,
    className,
    "aria-hidden": true,
    focusable: false,
  };

  if (React.isValidElement(icon)) {
    const element = icon as React.ReactElement<any>;
    return React.cloneElement(element, {
      ...iconProps,
      ...(element.props || {}),
    });
  }

  if (isRenderableIconComponent(icon)) {
    const Icon = icon as React.ElementType;
    return <Icon {...iconProps} />;
  }

  return <span className={className}>{icon}</span>;
}

function normalizeFeatures(
  category: CategoryDetails & Category,
  features?: CategoryHighlightsFeature[]
): NormalizedFeature[] {
  const source = features?.length ? features : ((category.subCategories || []) as CategoryHighlightsFeature[]);

  return source
    .map((feature, index) => {
      const title = getFeatureTitle(feature);

      return {
        id: feature.id || feature.slug || title || index,
        title,
        href: getFeatureHref(feature, category),
        image: getFeatureImage(feature),
        imageSrc: feature.imageSrc,
        imageAlt: feature.imageAlt,
        icon: feature.icon,
        iconSrc: feature.iconSrc,
        iconAlt: feature.iconAlt,
        accentColor: feature.accentColor,
      };
    })
    .filter((feature) => !!feature.title);
}

function normalizeBadges(
  badges: CategoryHighlightsBadge[] | undefined,
  points: CategoryPoint[] | undefined
): NormalizedBadge[] {
  const source = badges?.length
    ? badges
    : ((points || []) as Array<CategoryPoint & CategoryHighlightsBadge>);

  return source
    .slice(0, 3)
    .map((badge, index) => {
      const title = badge.label || badge.title || "";

      return {
        id: badge.id || title || index,
        title,
        text: badge.text,
        icon: badge.icon,
        iconSrc: badge.iconSrc,
        iconAlt: badge.iconAlt,
        accentColor: badge.accentColor,
      };
    })
    .filter((badge) => !!badge.title);
}

function CategoryHighlightsLink({
  LinkComponent,
  href,
  className,
  title,
  ariaLabel,
  children,
}: {
  LinkComponent?: CategoryHighlightsLinkComponent;
  href: string;
  className: string;
  title?: string;
  ariaLabel?: string;
  children: React.ReactNode;
}) {
  if (LinkComponent) {
    return (
      <LinkComponent href={href} className={className} title={title} aria-label={ariaLabel}>
        {children}
      </LinkComponent>
    );
  }

  return (
    <a href={href} className={className} title={title} aria-label={ariaLabel}>
      {children}
    </a>
  );
}

function FeatureTile({
  feature,
  category,
  LinkComponent,
}: {
  feature: NormalizedFeature;
  category: CategoryDetails & Category;
  LinkComponent?: CategoryHighlightsLinkComponent;
}) {
  const featureImageSrc = getImageSource(feature.image);
  const hasImage = !!featureImageSrc;
  const hasIcon = !hasImage && hasProvidedIcon(feature.iconSrc, feature.icon);
  const featureClasses = [
    "services-highlights-feature",
    hasImage ? "services-highlights-feature--with-image" : "",
    hasIcon ? "services-highlights-feature--with-icon" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const featureStyle = feature.accentColor
    ? ({ "--services-highlights-accent": feature.accentColor } as ServicesHighlightsCssVars)
    : undefined;

  return (
    <CategoryHighlightsLink
      LinkComponent={LinkComponent}
      href={feature.href}
      className="services-highlights-feature-link"
      title={feature.title}
      ariaLabel={`${feature.title} for ${category.name}`}
    >
      <article className={featureClasses} style={featureStyle}>
        {hasImage ? (
          <span className="services-highlights-feature-image-frame">
            <img
              src={featureImageSrc}
              alt={getImageAlt(feature.image, feature.title)}
              className="services-highlights-feature-image"
              loading={feature.image?.loading || "lazy"}
              decoding={feature.image?.decoding || "async"}
              style={
                feature.image?.objectPosition
                  ? { objectPosition: feature.image.objectPosition }
                  : undefined
              }
            />
          </span>
        ) : hasIcon ? (
          <span className="services-highlights-feature-icon" aria-hidden="true">
            {feature.iconSrc ? (
              <img
                src={feature.iconSrc}
                alt={feature.iconAlt || ""}
                className="services-highlights-icon-image"
                loading="lazy"
              />
            ) : (
              renderIcon(feature.icon, "services-highlights-icon-node", 23)
            )}
          </span>
        ) : null}
        <h3 className="services-highlights-feature-title">{feature.title}</h3>
      </article>
    </CategoryHighlightsLink>
  );
}

function BadgeTile({ badge }: { badge: NormalizedBadge }) {
  const hasIcon = hasProvidedIcon(badge.iconSrc, badge.icon);
  const badgeClasses = [
    "services-highlights-badge",
    hasIcon ? "services-highlights-badge--with-icon" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const badgeStyle = badge.accentColor
    ? ({ "--services-highlights-accent": badge.accentColor } as ServicesHighlightsCssVars)
    : undefined;

  return (
    <div className={badgeClasses} style={badgeStyle}>
      {hasIcon ? (
        <span className="services-highlights-badge-icon" aria-hidden="true">
          {badge.iconSrc ? (
            <img
              src={badge.iconSrc}
              alt={badge.iconAlt || ""}
              className="services-highlights-icon-image"
              loading="lazy"
            />
          ) : (
            renderIcon(badge.icon, "services-highlights-icon-node", 23)
          )}
        </span>
      ) : null}
      <div className="services-highlights-badge-text">
        <p className="services-highlights-badge-title">{badge.title}</p>
        {badge.text ? <p className="services-highlights-badge-copy">{badge.text}</p> : null}
      </div>
    </div>
  );
}

function LegacyServicesHighlightsLayout({
  category,
  alternate,
  classes,
  alternateBackground,
  hideServicesLinks,
  showPoints,
  hideTitle,
  hideSubtitle,
  showMainCategoryLink,
  overlineTitle,
  showThumbnails,
  showGalleryArrows,
}: CategoryHighlightsProps) {
  const { greaterThanTailwindMd768 } = useResizeHook();

  const gallery = (
    <ServicesHighlightsGallery
      category={category}
      showThumbnails={showThumbnails}
      showGalleryArrows={showGalleryArrows}
    />
  );

  const content = (
    <ServicesHighlightsContent
      category={category}
      hideServicesLinks={hideServicesLinks}
      hideTitle={hideTitle}
      showPoints={showPoints}
      overlineTitle={overlineTitle}
      hideSubtitle={hideSubtitle}
      alternateBackground={alternateBackground}
      showMainCategoryLink={showMainCategoryLink}
    />
  );

  return (
    <div className={classes}>
      <div className="grid grid-cols-1 justify-around items-center py-2 md:grid-cols-2 md:w-[calc(100%-10px)] md:mx-auto md:py-0">
        <div>{!greaterThanTailwindMd768 ? content : alternate ? content : gallery}</div>
        <div>{greaterThanTailwindMd768 ? (alternate ? gallery : content) : undefined}</div>
      </div>
    </div>
  );
}

const ServicesHighlights = ({
  category,
  alternate = false,
  classes = "",
  className = "",
  alternateBackground = false,
  hideServicesLinks = false,
  showContactsButton = false,
  showPoints = false,
  hideTitle = false,
  hideSubtitle = false,
  showMainCategoryLink = false,
  overlineTitle,
  showThumbnails = false,
  showGalleryArrows = false,
  variant = "feature-panel",
  LinkComponent,
  title,
  description,
  features,
  badges,
  primaryCta,
  secondaryCta,
  mainCategoryLinkLabel,
  image,
  eyebrowIcon,
  accentColor,
  backgroundColor,
  panelBackgroundColor = t.colors.panel,
  maxWidth = "xl",
  contentMaxWidth = t.sizes.contentMaxWidth,
  ariaLabel,
}: CategoryHighlightsProps) => {
  if (variant === "legacy") {
    return (
      <LegacyServicesHighlightsLayout
        category={category}
        alternate={alternate}
        classes={classes || className}
        alternateBackground={alternateBackground}
        hideServicesLinks={hideServicesLinks}
        showPoints={showPoints}
        hideTitle={hideTitle}
        hideSubtitle={hideSubtitle}
        showMainCategoryLink={showMainCategoryLink}
        overlineTitle={overlineTitle}
        showThumbnails={showThumbnails}
        showGalleryArrows={showGalleryArrows}
      />
    );
  }

  const resolvedTitle = hideTitle ? "" : title || category.name;
  const resolvedDescription = hideSubtitle ? "" : getDescription(category, description);
  const resolvedImage = getCategoryImage(category, image);
  const imageSrc = getImageSource(resolvedImage);
  const shouldUseGallery = (showThumbnails || showGalleryArrows) && !!category.images?.length;
  const hasMedia = shouldUseGallery || !!imageSrc;
  const resolvedFeatures = hideServicesLinks ? [] : normalizeFeatures(category, features);
  const resolvedBadges = normalizeBadges(badges, showPoints ? category.points : undefined);
  const primaryHref = primaryCta?.href || (showContactsButton ? "/contact" : "");
  const primaryLabel = primaryCta?.label || (showContactsButton ? "Get a Free Quote" : "");
  const secondaryHref = secondaryCta?.href || (showMainCategoryLink ? `/services/${category.slug}` : "");
  const secondaryLabel =
    secondaryCta?.label || (showMainCategoryLink ? mainCategoryLinkLabel || category.name : "");
  const resolvedAccentColor =
    accentColor || (category as CategoryDetails & Category & { accentColor?: string }).accentColor || t.colors.accent;
  const hasEyebrowIcon = hasIconContent(eyebrowIcon);
  const hasPrimaryIcon = hasProvidedIcon(primaryCta?.iconSrc, primaryCta?.icon);
  const hasSecondaryIcon = hasProvidedIcon(secondaryCta?.iconSrc, secondaryCta?.icon);
  const primaryCtaClasses = [
    "services-highlights-primary-cta",
    hasPrimaryIcon ? "services-highlights-primary-cta--with-icon" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const secondaryCtaClasses = [
    "services-highlights-secondary-cta",
    hasSecondaryIcon ? "services-highlights-secondary-cta--with-icon" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const sectionStyle: ServicesHighlightsCssVars = {
    "--services-highlights-bg": backgroundColor || (alternateBackground ? t.colors.panel : t.colors.background),
    "--services-highlights-panel-bg": panelBackgroundColor,
    "--services-highlights-accent": resolvedAccentColor,
    "--services-highlights-max-width": resolveMaxWidth(maxWidth),
    "--services-highlights-content-max-width": getCssSize(contentMaxWidth),
  };

  const rootClasses = [
    "services-highlights",
    alternate ? "services-highlights--alternate" : "",
    hasMedia ? "" : "services-highlights--no-media",
    classes,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={rootClasses}
      data-component="category-highlights"
      aria-label={ariaLabel || resolvedTitle || category.name}
      style={sectionStyle}
    >
      <style>{servicesHighlightsCss}</style>

      <div className="services-highlights-shell">
        <div className="services-highlights-panel">
          <div className="services-highlights-content">
            {overlineTitle ? (
              <p className="services-highlights-kicker">
                {hasEyebrowIcon ? (
                  <span className="services-highlights-kicker-icon" aria-hidden="true">
                    {renderIcon(eyebrowIcon, "services-highlights-icon-node", 24)}
                  </span>
                ) : null}
                {overlineTitle}
              </p>
            ) : null}

            {resolvedTitle ? <h2 className="services-highlights-title">{resolvedTitle}</h2> : null}
            {resolvedDescription ? <p className="services-highlights-description">{resolvedDescription}</p> : null}

            {resolvedFeatures.length ? (
              <div className="services-highlights-features" aria-label={`${category.name} services`}>
                {resolvedFeatures.map((feature) => (
                  <FeatureTile
                    key={feature.id}
                    feature={feature}
                    category={category}
                    LinkComponent={LinkComponent}
                  />
                ))}
              </div>
            ) : null}

            {primaryHref || secondaryHref ? (
              <div className="services-highlights-ctas">
                {primaryHref ? (
                  <CategoryHighlightsLink
                    LinkComponent={LinkComponent}
                    href={primaryHref}
                    className={primaryCtaClasses}
                    ariaLabel={primaryCta?.ariaLabel || primaryLabel}
                  >
                    {primaryLabel}
                    {hasPrimaryIcon ? (
                      <span className="services-highlights-primary-icon" aria-hidden="true">
                        {primaryCta?.iconSrc ? (
                          <img
                            src={primaryCta.iconSrc}
                            alt={primaryCta.iconAlt || ""}
                            className="services-highlights-icon-image"
                            loading="lazy"
                          />
                        ) : (
                          renderIcon(primaryCta?.icon, "services-highlights-icon-node", 18)
                        )}
                      </span>
                    ) : null}
                  </CategoryHighlightsLink>
                ) : null}

                {secondaryHref ? (
                  <CategoryHighlightsLink
                    LinkComponent={LinkComponent}
                    href={secondaryHref}
                    className={secondaryCtaClasses}
                    ariaLabel={secondaryCta?.ariaLabel || secondaryLabel}
                  >
                    {secondaryLabel}
                    {hasSecondaryIcon ? (
                      secondaryCta?.iconSrc ? (
                        <img
                          src={secondaryCta.iconSrc}
                          alt={secondaryCta.iconAlt || ""}
                          className="services-highlights-secondary-icon-image"
                          loading="lazy"
                          aria-hidden="true"
                        />
                      ) : (
                        renderIcon(secondaryCta?.icon, "services-highlights-secondary-icon-node", 18)
                      )
                    ) : null}
                  </CategoryHighlightsLink>
                ) : null}
              </div>
            ) : null}

            {!hasMedia && resolvedBadges.length ? (
              <div className="services-highlights-inline-badges">
                {resolvedBadges.map((badge) => (
                  <BadgeTile key={badge.id} badge={badge} />
                ))}
              </div>
            ) : null}
          </div>

          {hasMedia ? (
            <div className="services-highlights-media-column">
              <div className="services-highlights-media">
                {shouldUseGallery ? (
                  <div className="services-highlights-media-gallery">
                    <ServicesHighlightsGallery
                      category={category}
                      showThumbnails={showThumbnails}
                      showGalleryArrows={showGalleryArrows}
                      disableSwipe={false}
                    />
                  </div>
                ) : imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={getImageAlt(resolvedImage, resolvedTitle || category.name)}
                    className="services-highlights-image"
                    loading={resolvedImage?.loading || "lazy"}
                    decoding={resolvedImage?.decoding || "async"}
                  />
                ) : (
                  <div className="services-highlights-image-fallback">{resolvedTitle || category.name}</div>
                )}
              </div>

              {resolvedBadges.length ? (
                <div className="services-highlights-badges" aria-label={`${category.name} highlights`}>
                  {resolvedBadges.map((badge) => (
                    <BadgeTile key={badge.id} badge={badge} />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default ServicesHighlights;
